import { useT } from "@/i18n";

/**
 * THE WAIT, AS A PILL TURNING — the one loading mark for the whole app.
 *
 * Every wait on an answer draws this capsule turning end over end — the
 * brand's gold and sand halves, spun on the `pill-turn` keyframes shared with bootstrap
 * in `styles/pill-motion.css`. Three placements:
 *   page   — inside a route whose chrome is already drawn; centred in the
 *            screen's middle (`70svh` clears the bar and a tab strip).
 *   screen — the router's pending state, where nothing has drawn yet and the
 *            pill has the whole viewport.
 *   inline — inside a line or a small box (a dialogue, the logo's square);
 *            the caller positions it.
 * The word is still carried for a screen reader (`state.loading`); the pill
 * itself is decoration, and a reader who asks for reduced motion gets it
 * still. Before the app mounts, `index.html` draws the same capsule by hand.
 */
export function LoadingPill({ variant = "page" }: { variant?: "page" | "screen" | "inline" }) {
  const { t } = useT();
  const pill = (
    <span
      aria-hidden="true"
      className="flex h-5 w-10 animate-pill-turn overflow-hidden rounded-full border border-line-strong shadow-sm motion-reduce:animate-none"
    >
      <span className="h-full w-1/2 bg-brand-gold" />
      <span className="h-full w-1/2 bg-brand-sand" />
    </span>
  );
  if (variant === "inline") {
    return (
      <span role="status" className="inline-flex items-center">
        <span className="sr-only">{t("state.loading")}</span>
        {pill}
      </span>
    );
  }
  return (
    <div
      role="status"
      className={
        variant === "screen" ? "grid min-h-dvh place-items-center" : "grid min-h-[70svh] place-items-center"
      }
    >
      <span className="sr-only">{t("state.loading")}</span>
      {pill}
    </div>
  );
}
